// The motion system. Every shell transition goes through these tokens and the
// Web Animations API, so animations are cancellable and always end in the state
// the store says is true (rapid clicks never leave a half-animated window).

import { useEffect, useState } from 'react';
import { useSettings } from '../state/settings';

export const DUR = {
  open: 200,
  close: 150,
  minimize: 220,
  maximize: 220,
  menu: 150,
  reveal: 380,
};
export const EASE = {
  out: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  in: 'cubic-bezier(0.5, 0, 0.75, 0)',
  inout: 'cubic-bezier(0.65, 0, 0.35, 1)',
};

const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;

export function reducedMotion(): boolean {
  const pref = useSettings.getState().motion;
  if (pref === 'reduce') return true;
  if (pref === 'full') return false;
  return !!mq?.matches;
}

/** React hook that re-renders when either the system or in-app preference changes. */
export function useReducedMotion(): boolean {
  const pref = useSettings((s) => s.motion);
  const [sys, setSys] = useState(!!mq?.matches);
  useEffect(() => {
    if (!mq) return;
    const fn = () => setSys(mq.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return pref === 'reduce' || (pref === 'system' && sys);
}

/** Keep <html class="reduce-motion"> in sync for CSS-driven motion. */
export function useMotionClass() {
  const reduce = useReducedMotion();
  useEffect(() => {
    document.documentElement.classList.toggle('reduce-motion', reduce);
  }, [reduce]);
}

const running = new WeakMap<Element, Animation>();

/**
 * Run one animation on an element, cancelling whatever the shell was already
 * playing on it. Resolves true when it finished, false when it was superseded.
 */
export function play(el: Element, keyframes: Keyframe[], opts: KeyframeAnimationOptions): Promise<boolean> {
  running.get(el)?.cancel();
  running.delete(el);
  // No timeline to run on (background tab, old browser): jump to the end state.
  if (typeof (el as HTMLElement).animate !== 'function' || document.hidden) return Promise.resolve(true);
  const reduce = reducedMotion();
  const duration = reduce ? Math.min(90, Number(opts.duration) || 90) : Number(opts.duration) || 0;
  const anim = (el as HTMLElement).animate(reduce ? keyframes.map((k) => ({ opacity: k.opacity ?? 1 })) : keyframes, { ...opts, duration });
  // If the tab is hidden mid-animation the timeline freezes; timers still run, so
  // force completion and the window always reaches the state the store describes.
  setTimeout(() => anim.playState === 'running' && anim.finish(), duration + 300);
  // Keep the handle after it finishes: a `fill: forwards` animation must still be
  // cancellable by cancelMotion(), otherwise its end state would stick forever.
  running.set(el, anim);
  return anim.finished.then(
    () => running.get(el) === anim,
    () => false,
  );
}

export function cancelMotion(el: Element | null) {
  if (!el) return;
  running.get(el)?.cancel();
  running.delete(el);
}
